// File: frontend/src/components/Dashboard/DashboardStats.jsx
// Dashboard statistics cards component

import React from 'react';
import {
  UserGroupIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const DashboardStats = ({ data, userRole }) => {
  // Default stats structure based on role
  const getStatsForRole = () => {
    const baseStats = {
      totalLeads: data?.totalLeads || 0,
      qualifiedLeads: data?.qualifiedLeads || 0,
      pipelineValue: data?.pipelineValue || 0,
      conversionRate: data?.conversionRate || 0,
      totalContacts: data?.totalContacts || 0,
      totalCompanies: data?.totalCompanies || 0,
      todaysActivities: data?.todaysActivities || 0,
      thisMonthDeals: data?.thisMonthDeals || 0
    };

    // Stats configuration based on user role
    const roleStats = {
      'Super_Admin': [
        {
          name: 'Total Leads',
          value: baseStats.totalLeads.toLocaleString(),
          icon: UserGroupIcon,
          color: 'blue',
          change: data?.leadsChange ?? null,
          trend: (data?.leadsChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Leads Qualificados',
          value: baseStats.qualifiedLeads.toLocaleString(),
          icon: CheckCircleIcon,
          color: 'green',
          change: data?.qualifiedChange ?? null,
          trend: (data?.qualifiedChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Valor Pipeline',
          value: `R$ ${(baseStats.pipelineValue / 1000).toFixed(0)}k`,
          icon: CurrencyDollarIcon,
          color: 'yellow',
          change: data?.pipelineChange ?? null,
          trend: (data?.pipelineChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Taxa Conversão',
          value: `${baseStats.conversionRate.toFixed(1)}%`,
          icon: ChartBarIcon,
          color: 'purple',
          change: data?.conversionChange ?? null,
          trend: (data?.conversionChange ?? 0) >= 0 ? 'up' : 'down'
        }
      ],
      'administrator': [
        {
          name: 'Total Contatos',
          value: baseStats.totalContacts.toLocaleString(),
          icon: UserGroupIcon,
          color: 'blue',
          change: data?.contactsChange ?? null,
          trend: (data?.contactsChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Total Empresas',
          value: baseStats.totalCompanies.toLocaleString(),
          icon: BuildingOfficeIcon,
          color: 'indigo',
          change: data?.companiesChange ?? null,
          trend: (data?.companiesChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Atividades (30d)',
          value: baseStats.todaysActivities.toLocaleString(),
          icon: CalendarIcon,
          color: 'green',
          change: 0,
          trend: 'neutral'
        },
        {
          name: 'Negócios/Mês',
          value: baseStats.thisMonthDeals.toLocaleString(),
          icon: ChartBarIcon,
          color: 'orange',
          change: data?.dealsChange ?? null,
          trend: (data?.dealsChange ?? 0) >= 0 ? 'up' : 'down'
        }
      ],
      'Gerente_Vendas': [
        {
          name: 'Meus Leads',
          value: baseStats.totalLeads.toLocaleString(),
          icon: UserGroupIcon,
          color: 'blue',
          change: data?.leadsChange ?? null,
          trend: (data?.leadsChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Qualificados',
          value: baseStats.qualifiedLeads.toLocaleString(),
          icon: CheckCircleIcon,
          color: 'green',
          change: data?.qualifiedChange ?? null,
          trend: (data?.qualifiedChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Pipeline Value',
          value: `R$ ${(baseStats.pipelineValue / 1000).toFixed(0)}k`,
          icon: CurrencyDollarIcon,
          color: 'yellow',
          change: data?.pipelineChange ?? null,
          trend: (data?.pipelineChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Taxa Conversão',
          value: `${baseStats.conversionRate.toFixed(1)}%`,
          icon: ChartBarIcon,
          color: 'purple',
          change: data?.conversionChange ?? null,
          trend: (data?.conversionChange ?? 0) >= 0 ? 'up' : 'down'
        }
      ],
      'Vendedor': [
        {
          name: 'Meus Leads',
          value: baseStats.totalLeads.toLocaleString(),
          icon: UserGroupIcon,
          color: 'blue',
          change: data?.leadsChange ?? null,
          trend: (data?.leadsChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Qualificados',
          value: baseStats.qualifiedLeads.toLocaleString(),
          icon: CheckCircleIcon,
          color: 'green',
          change: data?.qualifiedChange ?? null,
          trend: (data?.qualifiedChange ?? 0) >= 0 ? 'up' : 'down'
        },
        {
          name: 'Atividades (30d)',
          value: baseStats.todaysActivities.toLocaleString(),
          icon: CalendarIcon,
          color: 'orange',
          change: 0,
          trend: 'neutral'
        },
        {
          name: 'Taxa Conversão',
          value: `${baseStats.conversionRate.toFixed(1)}%`,
          icon: ChartBarIcon,
          color: 'purple',
          change: data?.conversionChange ?? null,
          trend: (data?.conversionChange ?? 0) >= 0 ? 'up' : 'down'
        }
      ]
    };

    // Map real DB role names to the closest available stat set.
    // (Admin-tier roles get the admin view; sales/lead roles get the seller view.)
    const roleAliases = {
      'Admin_Marketing': 'administrator',
      'Marketing_Digital': 'administrator',
      'Especialista_Leads': 'Vendedor',
    };
    const resolvedRole = roleStats[userRole]
      ? userRole
      : (roleAliases[userRole] || 'Vendedor');

    return roleStats[resolvedRole] || roleStats['Vendedor'];
  };

  const stats = getStatsForRole();

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      indigo: 'bg-indigo-50 text-indigo-600'
    };
    return colorMap[color] || colorMap.blue;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return ArrowTrendingUpIcon;
    if (trend === 'down') return ArrowTrendingDownIcon;
    return null;
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-400';
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
      {stats.map((stat, index) => {
        const IconComponent = stat.icon;
        const TrendIcon = getTrendIcon(stat.trend);
        
        return (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${getColorClasses(stat.color)}`}>
                  <IconComponent className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
              </div>

              {/* Trend indicator (only when the backend actually provides a change) */}
              {TrendIcon && stat.change != null && stat.change !== 0 && (
                <div className={`flex items-center ${getTrendColor(stat.trend)}`}>
                  <TrendIcon className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">
                    {Math.abs(stat.change).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
            
            {/* Progress bar for conversion rate */}
            {stat.name.includes('Conversão') && (
              <div className="mt-4">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(parseFloat(stat.value), 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DashboardStats;